import { Node } from '@tiptap/core';

import { QuillMentionMatch, QuillMentionUtils } from '@spaces-ui/meadow/helpers/quill-helpers';

export type ChipNodeAttrs = QuillMentionMatch;

export const ChipNodeExtension = Node.create({
  name: 'chip',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      id: { default: null },
      value: { default: null },
      avatarUrl: { default: undefined },
      avatarColor: { default: undefined },
      avatarShape: { default: undefined },
      svgIcon: { default: undefined },
      svgIconClass: { default: undefined },
      additionalClass: { default: undefined },
      listItemDisplay: { default: undefined },
      listItemDescription: { default: undefined },
      statusSvg: { default: undefined },
      isConnectedUser: { default: undefined },
      isGuestUser: { default: undefined },
      secondaryValue: { default: undefined },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.mdw-quill-chip',
        getAttrs: node => {
          const el = node as HTMLElement;
          const attrs: Record<string, unknown> = {};

          for (const key of Object.keys(el.dataset)) {
            attrs[key] = el.dataset[key];
          }

          // Convert numeric strings back to numbers
          if (attrs['avatarColor'] !== undefined) {
            attrs['avatarColor'] = Number(attrs['avatarColor']);
          }

          // Convert boolean strings back to booleans
          if (attrs['isConnectedUser'] !== undefined) {
            attrs['isConnectedUser'] = attrs['isConnectedUser'] === 'true';
          }
          if (attrs['isGuestUser'] !== undefined) {
            attrs['isGuestUser'] = attrs['isGuestUser'] === 'true';
          }

          return attrs;
        },
      },
    ];
  },

  renderHTML({ node }) {
    const dataAttrs: Record<string, string> = {};

    for (const [key, val] of Object.entries(node.attrs)) {
      if (val !== undefined && val !== null) {
        // Kebab-case to mirror how the node view's `dataset` writes render, so
        // parseHTML (which reads `el.dataset`) can round-trip serialized chips.
        const attrName = key.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`);
        dataAttrs[`data-${attrName}`] = `${val}`;
      }
    }

    return ['div', { class: 'mdw-quill-chip', contenteditable: 'false', ...dataAttrs }];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.classList.add('mdw-quill-chip');
      dom.contentEditable = 'false';

      const data = node.attrs as ChipNodeAttrs;

      // Set data attributes for round-tripping
      for (const [key, val] of Object.entries(data)) {
        if (val !== undefined && val !== null) {
          dom.dataset[key] = `${val}`;
        }
      }

      const showAvatar =
        data.avatarColor !== undefined ||
        data.avatarUrl !== undefined ||
        data.svgIcon !== undefined;
      const mentionEl = QuillMentionUtils.RenderMcMentionItem(data, showAvatar);
      mentionEl.classList.add('spaces-quill-mention-item');
      dom.appendChild(mentionEl);

      return { dom };
    };
  },
});
